import * as React from 'react'
import {ReactElement} from 'react'
import {NatOwnershipTransfersRepo} from '../repository/NatOwnershipTransfersRepo'
import {Box} from '@mui/material'
import {NatOwnershipTransferIntermediaryLocationState} from './intermediary/NatGuestIntermediaryInterface'
import {Messages} from '../../../../Messages'
import NatCondominiumRepo from '../../condominium/repository/NatCondominiumRepo'
import NatCurrentDateTimeRepo from '../../currentDateTime/repository/NatCurrentDateTimeRepo'
import HolidaysRepository from '../../../../components/HolidaysRepository'
import {Nullable} from '../../../../app/TypeHelpers'
import {NatPostIntermediaryRequest, NatPostIntermediaryResponse} from '../repository/NatPostIntermediaryRequest'
import {BlockSpacer} from '../../../../styledComponents/ResponsiveConvenience'
import {DisplayItemDividerForPageTop} from '../../../../styledComponents/DisplayItem'
import {PageTitle} from '../../applications/common/resident/NatResidentApplicationsCommon'
import {
    HeadingAreaMessageComponentForResident,
} from '../../../../components/shared/pageBaseComponents/HeadingAreaMessageComponentForResident'
import NatGuestIntermediaryForm from './intermediary/NatGuestIntermediaryForm'
import {
    createGuestWithErrorHandlingPage,
    GuestWithErrorHandlingPagePropsSet,
} from '../../../../components/shared/hoc/createGuestWithErrorHandlingPage'
import {GuestPlainLinkStyledBackButton} from '../common/NatOwnershipTransfersCommon'
import {Location} from 'react-router-dom'

interface Props {
    condominiumCd: string
    applyId?: string
    ownershipTransfersRepo: NatOwnershipTransfersRepo
    condominiumRepo: NatCondominiumRepo
    currentDateTimeRepo: NatCurrentDateTimeRepo
    holidaysRepo: HolidaysRepository
    location: Location<NatOwnershipTransferIntermediaryLocationState>
}

interface State {
    ownershipTransferDate: Nullable<string>
    companyName: Nullable<string>
    zipCode: Nullable<string>
    prefecture: Nullable<string>
    address2: Nullable<string>
    address3: Nullable<string>
    buildingRoomNumber: Nullable<string>
    representative: Nullable<string>
    phoneNumber: Nullable<string>
    mailAddress: Nullable<string>
    mailAddressConfirm: Nullable<string>
    version: Nullable<number>
    isLoadCompleted: boolean
}

class NatGuestIntermediaryInputPage
    extends React.Component<Props & GuestWithErrorHandlingPagePropsSet, State> {

    constructor(props: Props & GuestWithErrorHandlingPagePropsSet) {
        super(props)

        this.state = {
            ownershipTransferDate: null,
            companyName: null,
            zipCode: null,
            prefecture: null,
            address2: null,
            address3: null,
            buildingRoomNumber: null,
            representative: null,
            phoneNumber: null,
            mailAddress: null,
            mailAddressConfirm: null,
            version: 0,
            isLoadCompleted: false,
        }

        this.onClickApplyConfirmButton = this.onClickApplyConfirmButton.bind(this)
        this.onClickBackButton = this.onClickBackButton.bind(this)
    }

    async componentDidMount(): Promise<void> {
        const applyInfo = this.props.location.state?.applyInfo

        this.setState({
            ownershipTransferDate: applyInfo?.ownershipTransferDate ?? null,
            companyName: applyInfo?.companyName ?? null,
            zipCode: applyInfo?.zipCode ?? null,
            prefecture: applyInfo?.prefecture ?? null,
            address2: applyInfo?.address2 ?? null,
            address3: applyInfo?.address3 ?? null,
            buildingRoomNumber: applyInfo?.buildingRoomNumber ?? null,
            representative: applyInfo?.representative ?? null,
            phoneNumber: applyInfo?.phoneNumber ?? null,
            mailAddress: applyInfo?.mailAddress ?? null,
            mailAddressConfirm: null,
            version: applyInfo?.version ?? null,
            isLoadCompleted: true,
        })
    }

    onClickBackButton(): void {
        let path = ''
        if (this.props.applyId) {
            path = `/guest/condominiums/${this.props.condominiumCd}/applications/ownership-transfers/intermediary/confirm/${this.props.applyId}`
        } else {
            path = `/guest/condominiums/${this.props.condominiumCd}/applications/ownership-transfers/intermediary/agreement`
        }
        this.props.navigate(path, {
            replace: true,
            state: {
                isCompleteFlow: this.props.location.state?.isCompleteFlow,
            },
        })
    }

    async onClickApplyConfirmButton(request: NatPostIntermediaryRequest): Promise<void> {
        const {ownershipTransfersRepo, condominiumCd} = this.props

        await this.props.eventEmitter.showLoading()
        try {
            const response: Nullable<NatPostIntermediaryResponse> =
                await this.props.asyncCallFunction(
                    ownershipTransfersRepo.applyOwnershipTransferIntermediary.bind(
                        ownershipTransfersRepo, condominiumCd, request),
                    async (httpErrorOrNetworkError) => {
                        await httpErrorOrNetworkError.broadcast(this.props.eventEmitter)
                    },
                    {
                        defaultErrorTitle: Messages.EOPCR0014.toString(),
                        defaultErrorTitlePriority: true,
                        replacementDetailErrorMessages: [{
                            targetMessageId: Messages.EOPCA0004,
                            replacementMessageId: Messages.EOPCR0015,
                        }],
                    },
                )

            if (response && response.requestError === null) {
                const locationState: NatOwnershipTransferIntermediaryLocationState = {
                    applyInfo: {
                        ownershipTransferDate: request.ownershipTransferDate,
                        companyName: request.companyName,
                        accessExpirationDate: this.props.location.state.applyInfo?.accessExpirationDate,
                        zipCode: request.zipCode,
                        prefecture: request.prefecture,
                        address2: request.address2,
                        address3: request.address3,
                        buildingRoomNumber: request.buildingRoomNumber,
                        representative: request.representative,
                        phoneNumber: request.phoneNumber,
                        mailAddress: request.mailAddress,
                        mailAddressConfirm: request.mailAddressConfirm,
                        version: request.version,
                    },
                    isApply: true,
                    isCompleteFlow: this.props.location.state?.isCompleteFlow,
                }
                this.props.navigate(
                    `/guest/condominiums/${this.props.condominiumCd}/applications/ownership-transfers/intermediary/confirm/${this.props.applyId ?? ''}`,
                    {
                        replace: true,
                        state: JSON.parse(JSON.stringify(locationState)),
                    },
                )
            }
        } finally {
            this.props.eventEmitter.closeLoading()
        }
    }

    render(): ReactElement {
        if (!this.state.isLoadCompleted) {
            return (<></>)
        }

        return (
            <>
                <GuestPlainLinkStyledBackButton onClick={this.onClickBackButton}>戻る</GuestPlainLinkStyledBackButton>
                <BlockSpacer height={'16px'}/>
                <DisplayItemDividerForPageTop/>
                <BlockSpacer height={'30px'}/>
                <PageTitle>
                    組合員資格変更届
                </PageTitle>
                <Box height={'20px'}/>
                <HeadingAreaMessageComponentForResident eventEmitter={this.props.eventEmitter}/>
                <NatGuestIntermediaryForm
                    {...this.props}
                    {...this.state}
                    submitButtonLabel={'申請内容を確認する'}
                    onClickApplyConfirmButton={this.onClickApplyConfirmButton}
                    onClickBackButton={this.onClickBackButton}
                />
            </>
        )
    }
}

export default createGuestWithErrorHandlingPage(NatGuestIntermediaryInputPage)
